import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    ApproveAuditInvoiceSummary,
    AuditInvoiceSummaryActionFailure,
    CreateAuditInvoiceSummaryNote,
    LoadAllAuditInvoiceSummaries,
    LoadAllAuditInvoiceSummariesFailure,
    LoadAllAuditInvoiceSummariesSuccess,
    LoadAuditInvoiceSummary,
    LoadAuditInvoiceSummarySuccess,
    MarkAuditInvoiceSummaryDocumentAsRead,
    RejectAuditInvoiceSummary,
    RemoveAuditInvoiceSummaryNote
} from '../actions/audit-invoice-summary.actions';
import { catchError, map, mergeMap, of } from 'rxjs';
import { AuditInvoiceSummaryControllerService, InvoiceControllerService } from '@iota/iota-management-portal-v4-client';

@Injectable()
export class AuditInvoiceSummaryEffects {
    loadAllAuditInvoiceSummaries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadAllAuditInvoiceSummaries),
            mergeMap(({ from, to }) =>
                this.apiService.getAllAuditInvoiceSummaries(from ? new Date(from) : undefined, to ? new Date(to) : undefined).pipe(
                    map((auditInvoiceSummaries) =>
                        LoadAllAuditInvoiceSummariesSuccess({ auditInvoiceSummaries: auditInvoiceSummaries ?? [] })
                    ),
                    catchError((error) => of(LoadAllAuditInvoiceSummariesFailure({ error })))
                )
            )
        )
    );

    loadAuditInvoiceSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadAuditInvoiceSummary),
            mergeMap(({ auditInvoiceSummaryId }) =>
                this.apiService.getAuditInvoiceSummary(auditInvoiceSummaryId).pipe(
                    map((auditInvoiceSummary) => LoadAuditInvoiceSummarySuccess({ auditInvoiceSummary })),
                    catchError((error) => of(LoadAllAuditInvoiceSummariesFailure({ error })))
                )
            )
        )
    );

    approveAuditInvoiceSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ApproveAuditInvoiceSummary),
            mergeMap(({ auditInvoiceSummaryId }) =>
                this.invoiceApiService.approveInvoice(auditInvoiceSummaryId).pipe(
                    map(() => LoadAuditInvoiceSummary({ auditInvoiceSummaryId })),
                    catchError((error) => of(AuditInvoiceSummaryActionFailure({ auditInvoiceSummaryId, error })))
                )
            )
        )
    );

    rejectAuditInvoiceSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RejectAuditInvoiceSummary),
            mergeMap(({ auditInvoiceSummaryId }) =>
                this.invoiceApiService.rejectInvoice(auditInvoiceSummaryId).pipe(
                    map(() => LoadAuditInvoiceSummary({ auditInvoiceSummaryId })),
                    catchError((error) => of(AuditInvoiceSummaryActionFailure({ auditInvoiceSummaryId, error })))
                )
            )
        )
    );

    createAuditInvoiceSummaryNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreateAuditInvoiceSummaryNote),
            mergeMap(({ auditInvoiceSummaryId, note }) =>
                this.apiService.createAuditInvoiceSummaryNote(auditInvoiceSummaryId, note).pipe(
                    map(() => LoadAuditInvoiceSummary({ auditInvoiceSummaryId })),
                    catchError((error) => of(AuditInvoiceSummaryActionFailure({ auditInvoiceSummaryId, error })))
                )
            )
        )
    );

    removeAuditInvoiceSummaryNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveAuditInvoiceSummaryNote),
            mergeMap(({ auditInvoiceSummaryId, noteId }) =>
                this.apiService.removeAuditInvoiceSummaryNote(auditInvoiceSummaryId, noteId).pipe(
                    map(() => LoadAuditInvoiceSummary({ auditInvoiceSummaryId })),
                    catchError((error) => of(AuditInvoiceSummaryActionFailure({ auditInvoiceSummaryId, error })))
                )
            )
        )
    );

    markAuditInvoiceSummaryDocumentAsRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MarkAuditInvoiceSummaryDocumentAsRead),
            mergeMap(({ auditInvoiceSummaryId, documentDescriptionId }) =>
                this.apiService.markAuditInvoiceSummaryAttachmentAsRead(auditInvoiceSummaryId, documentDescriptionId).pipe(
                    map(() => LoadAuditInvoiceSummary({ auditInvoiceSummaryId })),
                    catchError((error) => of(AuditInvoiceSummaryActionFailure({ auditInvoiceSummaryId, error })))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private apiService: AuditInvoiceSummaryControllerService,
        private invoiceApiService: InvoiceControllerService
    ) {}
}
